export type RamonStage = "egg" | "hatch" | "chick";
export type RamonAction = "idle" | "discover" | "confirm" | "hatch" | "hello" | "hello_short" | "curious" | "acknowledge" | "celebrate" | "proud" | "point_left" | "point_right" | "dance" | "welcome_back";

export interface RamonProps {
  item?: "bow" | "glasses" | "skirt" | null;
  stage: RamonStage;
  action: RamonAction;
  className?: string;
  label?: string;
  /** Forces a fresh playback when the product action is intentionally repeated. */
  playbackId?: number;
  /** Keeps the exact next clip warm without changing the current animation. */
  preloadAction?: RamonAction;
  /** Development-only latency grouping; never sent to analytics. */
  timingSource?: "home" | "onboarding" | "personalizar";
}

export const RAMON_RENDERER: "video" | "rive" = import.meta.env.VITE_RAMON_RENDERER === "rive" ? "rive" : "video";
export const IDLE_BY_STAGE: Partial<Record<RamonStage, RamonAction>> = { egg: "idle", chick: "idle" };
export const FALLBACK_BY_STAGE: Record<RamonStage, string> = {
  egg: "/gema/egg-placeholder.svg", hatch: "/gema/hatch-placeholder.svg", chick: "/gema/gema-placeholder.svg",
};

const VIDEO_NAMES: Record<RamonStage, Partial<Record<RamonAction, string>>> = {
  egg: { idle: "egg-idle", discover: "egg-discover", confirm: "egg-confirm" },
  hatch: { hatch: "hatch" },
  chick: {
    idle: "idle", hello: "hello", hello_short: "hello-short", curious: "curious",
    acknowledge: "acknowledge", celebrate: "celebrate", proud: "proud",
    point_left: "point-left", point_right: "point-right", dance: "dance", welcome_back: "welcome-back-v31",
  },
};
const LOOPING = new Set(["egg-idle", "idle", "dance"]);

export interface RamonVideoClip { webm: string; mov: string; poster: string; loop: boolean }

export function getVideoClip(stage: RamonStage, action: RamonAction, item?: "bow" | "glasses" | "skirt" | null): RamonVideoClip {
  const idle = IDLE_BY_STAGE[stage];
  const name = VIDEO_NAMES[stage][action] ?? (idle ? VIDEO_NAMES[stage][idle] : undefined) ?? "hatch";
  const root = `/gema/video/${stage === "chick" && item ? item + "/" : ""}${name}`;
  const revision = "?v=collection4";
  return { webm: `${root}.webm${revision}`, mov: `${root}.mov?v=apple-alpha5`, poster: `${root}.png${revision}`, loop: LOOPING.has(name) };
}

export const RAMON_RIVE_RUNTIME = {
  source: "/ramon/ramon.riv",
  artboards: { egg: "Ramon — casca viva v13", hatch: "Ramon — rompimento v17", chick: "Ramon v05 — pupila e pés" },
  animations: {
    egg: { idle: "egg-wait-v14", discover: "egg-emerge-v13", confirm: "egg-confirm-v14" },
    hatch: { hatch: "hatch-v17" },
    chick: {
      idle: "idle-detail-v10", hello: "hello-detail-v10", hello_short: "hello-short-detail-v10",
      curious: "curious-detail-v10", acknowledge: "acknowledge-detail-v10", celebrate: "celebrate-detail-v10",
      proud: "proud-detail-v10", point_left: "point-left-detail-v10", point_right: "point-right-detail-v10", dance: "dance-v28", welcome_back: "welcome-back-v30",
    },
  },
  durationMs: {
    confirm: 1_200, hatch: 3_400, hello: 4_200, hello_short: 1_600, curious: 2_000,
    acknowledge: 800, celebrate: 2_400, proud: 1_800, point_left: 3_200, point_right: 3_200, welcome_back: 4_400,
  } satisfies Partial<Record<RamonAction, number>>,
} as const;

export function animationFor(stage: RamonStage, action: RamonAction): string | undefined {
  const animations = RAMON_RIVE_RUNTIME.animations[stage] as Partial<Record<RamonAction, string>>;
  const idle = IDLE_BY_STAGE[stage];
  return animations[action] ?? (idle ? animations[idle] : undefined);
}
